import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UserPlus, UserCheck } from 'lucide-react';
import { get, post, del } from '@/api/client';
import { Button } from '@/components/atoms/Button';
import { useAuthStore } from '@/store/authStore';
import { useUIStore } from '@/store/uiStore';
import { confirmModal } from '@/store/confirmStore';

interface Props {
  userId: string;
  /** "icon" = 아이콘만 (게시글 상세), "full" = 텍스트 포함 (프로필) */
  variant?: 'icon' | 'full';
}

export function FollowToggle({ userId, variant = 'icon' }: Props) {
  const { isLoggedIn } = useAuthStore();
  const { setLoginModalOpen } = useUIStore();
  const queryClient = useQueryClient();

  const { data, isLoading: isStatusLoading } = useQuery({
    queryKey: ['follow', 'status', userId],
    queryFn: () => get<{ isFollowing: boolean }>(`/follow/${userId}/status`),
    enabled: isLoggedIn,
    staleTime: 30_000,
  });

  const isFollowing = data?.isFollowing ?? false;
  // 로그인 사용자가 상태 로딩 중 → 버튼을 stable skeleton 으로 표시해 깜빡임(팔로우 ↔ 언팔로우) 방지
  const showSkeleton = isLoggedIn && isStatusLoading;

  const followMutation = useMutation({
    mutationFn: () => post(`/follow/${userId}`),
    onSuccess: () => {
      queryClient.setQueryData(['follow', 'status', userId], { isFollowing: true });
      queryClient.invalidateQueries({ queryKey: ['users', userId] });
    },
  });

  const unfollowMutation = useMutation({
    mutationFn: () => del(`/follow/${userId}`),
    onSuccess: () => {
      queryClient.setQueryData(['follow', 'status', userId], { isFollowing: false });
      queryClient.invalidateQueries({ queryKey: ['users', userId] });
    },
  });

  const isPending = followMutation.isPending || unfollowMutation.isPending;

  const handleClick = async () => {
    if (!isLoggedIn) return setLoginModalOpen(true);
    if (isFollowing) {
      const ok = await confirmModal({
        title: '팔로우 취소',
        message: '팔로우를 취소할까요?',
        confirmLabel: '취소하기',
        cancelLabel: '유지',
      });
      if (!ok) return;
      unfollowMutation.mutate();
    } else {
      followMutation.mutate();
    }
  };

  if (variant === 'icon') {
    // 아이콘 + 텍스트 (게시글 상세 헤더 등) — text-sm 버튼
    // 팔로우("팔로우") ↔ 언팔로우("언팔로우") 전환 시 버튼 너비 고정 → 옆 버튼(신고 등) 위치 흔들림 방지
    if (showSkeleton) {
      return <div className="h-8 w-[88px] shrink-0 animate-pulse rounded-lg bg-[var(--color-bg)]" />;
    }
    return (
      <Button
        variant="ghost"
        size="sm"
        aria-label={isFollowing ? '언팔로우' : '팔로우'}
        onClick={handleClick}
        loading={isPending}
        className={`flex w-[88px] shrink-0 items-center justify-center gap-1 text-sm ${
          isFollowing ? 'text-[var(--color-primary)]' : ''
        }`}
      >
        {isFollowing ? (
          <>
            <UserCheck className="h-4 w-4" />
            언팔로우
          </>
        ) : (
          <>
            <UserPlus className="h-4 w-4" />
            팔로우
          </>
        )}
      </Button>
    );
  }

  if (showSkeleton) {
    return <div className="h-10 w-full animate-pulse rounded-lg bg-[var(--color-bg)]" />;
  }

  return (
    <Button
      fullWidth
      variant={isFollowing ? 'secondary' : 'primary'}
      onClick={handleClick}
      loading={isPending}
    >
      {isFollowing ? (
        <>
          <UserCheck className="h-4 w-4" />
          언팔로우
        </>
      ) : (
        <>
          <UserPlus className="h-4 w-4" />
          팔로우
        </>
      )}
    </Button>
  );
}
